import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { Button, Grid } from 'antd';
import { motion } from 'framer-motion';
import type React from 'react';
import { useState } from 'react';

const { useBreakpoint } = Grid;

interface TocItem {
  id: string;
  title: string;
  type?: string;
  hasChildren?: boolean;
}

interface TableOfContentsProps {
  tocItems: TocItem[];
  activeId: string;
  scrollToSection: (id: string) => void;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({
  tocItems,
  activeId,
  scrollToSection,
}) => {
  const screens = useBreakpoint();
  const [collapsed, setCollapsed] = useState(true);

  const isMobile = !screens.md;

  return (
    <div
      className={`${
        isMobile && !collapsed ? 'min-h-[calc(100vh)]' : ''
      } bg-grayZl-950 md:bg-transparent   z-10`}
    >
      <div
        onClick={() => {
          setCollapsed(!collapsed);
        }}
        className='flex justify-between items-center md:mb-2.5 px-4 py-1.5 bg-grayZl-950 md:bg-transparent z-10'
      >
        <h3 className='text-white font-semibold text-sm md:text-xl xl:text-2xl whitespace-nowrap'>
          Table of Contents
        </h3>
        {isMobile && (
          <Button
            type='text'
            icon={
              collapsed ? (
                <ChevronUpIcon className='w-4 h-4' />
              ) : (
                <ChevronDownIcon className='w-4 h-4' />
              )
            }
            className='text-white'
          />
        )}
      </div>

      {(!isMobile || !collapsed) && (
        <div className='relative bg-grayZl-950 md:bg-transparent h-full'>
          <motion.div
            className='absolute left-9 md:left-8  top-0 bottom-0 w-px bg-gray-700 '
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className='pl-14  space-y-3'
            layout
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {tocItems.map((item) => {
              const isActive =
                item.type === 'introduction' || item.id === activeId;

              return (
                <motion.div
                  key={item.id}
                  className='flex items-center cursor-pointer relative'
                  onClick={() => {
                    scrollToSection(item.id);
                    setCollapsed(!collapsed);
                  }}
                  whileHover={{
                    x: 2,
                    transition: {
                      type: 'spring',
                      stiffness: 400,
                      damping: 25,
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                >
                  {isActive && (
                    <motion.span
                      className='absolute -left-7 text-primary py-2 text-sm md:text-base xl:text-xl bg-grayZl-950  md:bg-black'
                      initial={{ scale: 0, x: -10, opacity: 0 }}
                      animate={{
                        scale: 1,
                        x: 0,
                        opacity: 1,
                        rotate: [0, 5, 0],
                      }}
                      exit={{ scale: 0, x: -10, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 25,
                        rotate: { duration: 0.6, ease: 'easeInOut' },
                      }}
                    >
                      &#9654;
                    </motion.span>
                  )}

                  <motion.span
                    className='text-sm xl:text-base font-medium truncate w-[150px] ml-1 '
                    animate={{
                      color: isActive ? 'white' : '#9F9EA6',
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    {item.title}
                  </motion.span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TableOfContents;
